import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';

  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!token) {
      setError('Token inválido. Solicita un nuevo enlace.');
      return;
    }
    if (pw.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }
    if (pw !== pw2) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setSuccess(true);
      setTimeout(() => navigate('/admin/login', { replace: true }), 2500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'No se pudo restablecer la contraseña.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-red-900 px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-red-600/20 backdrop-blur-sm border border-red-500/30 mb-4">
            <Lock size={32} className="text-red-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Nueva contraseña</h1>
          <p className="text-slate-400 text-sm">DRYWALL DESING LLC</p>
        </div>

        <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur">
          <CardHeader className="pb-4">
            <CardTitle className="text-xl text-slate-900">Crear nueva contraseña</CardTitle>
            <p className="text-sm text-slate-500">
              Elige una contraseña segura de al menos 8 caracteres.
            </p>
          </CardHeader>
          <CardContent>
            {success ? (
              <div
                data-testid="reset-success"
                className="flex items-start gap-2 p-4 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm"
              >
                <CheckCircle size={18} className="flex-shrink-0 mt-0.5" />
                <span>
                  ¡Contraseña actualizada con éxito! Redirigiendo al inicio de sesión...
                </span>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-form">
                {error && (
                  <div
                    data-testid="reset-error"
                    className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"
                  >
                    <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}
                {!token && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                    Falta el token de recuperación en el enlace. Solicita uno nuevo desde "Olvidé mi
                    contraseña".
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="new-pw">Nueva contraseña</Label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      id="new-pw"
                      data-testid="new-password-input"
                      type={show ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={pw}
                      onChange={(e) => setPw(e.target.value)}
                      placeholder="Mínimo 8 caracteres"
                      className="pl-10 pr-10"
                      disabled={submitting}
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShow((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                    >
                      {show ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-pw2">Repetir contraseña</Label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      id="new-pw2"
                      data-testid="new-password-confirm"
                      type={show ? 'text' : 'password'}
                      autoComplete="new-password"
                      value={pw2}
                      onChange={(e) => setPw2(e.target.value)}
                      placeholder="Repite la contraseña"
                      className="pl-10"
                      disabled={submitting}
                      required
                      minLength={8}
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  data-testid="reset-submit"
                  disabled={submitting || !token}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-3 font-semibold"
                >
                  {submitting ? 'Actualizando...' : 'Guardar nueva contraseña'}
                </Button>
              </form>
            )}

            <Link
              to="/admin/login"
              className="mt-6 inline-flex items-center gap-2 text-sm text-slate-600 hover:text-red-600 transition-colors"
            >
              <ArrowLeft size={14} /> Volver al inicio de sesión
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
